import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { JourneyHeaderWithAction } from '@/features/journey/admin/atoms/JourneyHeaderWithActions';
import { SEARCH_PARAM_KEYS, usePageState } from '@/features/management/manager/hooks/usePageState';
import { ManagementSearchPhrase } from '@/features/management/manager/atoms/ManagementSearchPhrase';
import { useLearningItemsColumnsDeclaration, SortBy } from '@/features/journey/admin/hooks/useLearningItemsColumnsDeclaration';
import { AutoTable, AutoTableProps } from '@/ui/AutoTable';
import { JourneyInsightsTabsLearningItemsTypeFilter } from '@/features/journey/admin/atoms/JourneyInsightsTabsLearningItemsTypeFilter';
import { useLazyLearningItems, LearningItemType } from '@/features/journey/admin/hooks/useLazyLearningItems';
import { RLearning_Journeys_Items_Insights_Order_By, useGenerateLearningJourneyItemsCsvMutationRemote } from '@/store/v2';
import { useManagedTeams } from '@/features/management/manager/hooks/useManagedTeams';
import { useIsManagementContext } from '@/features/management/manager/hooks/useIsManagementContext';
import { ManagementEmptyStateAfterProcessed } from '@/features/management/manager/atoms/ManagementEmptyStateAfterProcessed';
import { useQueryNumberArrayState } from '@/hooks/useQueryState';
import { JourneyInsightsTabsLearningItemsTableLoadingState } from '@/features/journey/admin/atoms/JourneyInsightsTabsLearningItemsTableLoadingState';

type LearningItemRow = LearningItemType & { learningType: string };

const ContainerStyled = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ITEMS_LIMIT = 20;

export const JourneyInsightsTabsLearningItems = ({ spaceId }: { spaceId: number }): React.ReactElement => {
  const { journeyId } = useParams<{ journeyId: string }>();
  const [selectedTeamIds] = useQueryNumberArrayState(SEARCH_PARAM_KEYS.SELECTED_TEAM_IDS, []);
  const journeyURN = journeyId ? `urn:collegial:self:journey:${journeyId}` : null;
  const columnDeclaration = useLearningItemsColumnsDeclaration();
  const [downloadCsvLink, { loading: downloadCsvLinkLoading }] = useGenerateLearningJourneyItemsCsvMutationRemote();
  const isManagementContext = useIsManagementContext();

  const {
    searchPhrase,
    setSearchPhrase,
    sortBy,
    sortOrder,
    onSortChange,
    page,
    setPage,
    onClearFilterState,
    selectedLearningType,
    setSelectedLearningType,
  } = usePageState<SortBy>('item_order');

  const teams = useManagedTeams({
    skip: !isManagementContext,
  });

  const { lazyLearningItems, totalCount, loading } = useLazyLearningItems({
    limit: ITEMS_LIMIT,
    offset: page * ITEMS_LIMIT,
    sortBy,
    orderBy: [{ [sortBy]: sortOrder }] as RLearning_Journeys_Items_Insights_Order_By,
    args: {
      learning_journey_urn: journeyURN as string,
      ...(!!selectedTeamIds?.length
        ? { team_ids: selectedTeamIds }
        : isManagementContext
          ? { team_ids: teams?.data?.map((team) => team.id) }
          : {}),
      ...(!!searchPhrase ? { title: searchPhrase } : {}),
      ...(!!selectedLearningType ? { type: selectedLearningType } : {}),
    },
  });

  const handleDownloadCSVClick = async () => {
    const { data } = await downloadCsvLink({
      variables: {
        input: {
          teamIds: isManagementContext ? teams?.data?.map((team) => team.id) : undefined,
          urn: journeyURN as string,
          spaceId,
        },
      },
    });
    const csvLink = data?.generateLearningJourneyItemsCSV?.presignedURL;

    if (csvLink) {
      window.open(csvLink, '_blank');
    }
  };

  const tableProps: AutoTableProps<LearningItemRow, SortBy> = {
    rows: lazyLearningItems?.length ? lazyLearningItems?.map((item) => ({ ...item, learningType: item?.type })) : [],
    rowKey: (learningItem) => learningItem.urn,
    columnDeclaration,
    pageCount: Math.ceil((totalCount ?? 0) / ITEMS_LIMIT),
    page,
    onPageChange: (event) => {
      setPage(event);
    },
    currentSortKey: sortBy,
    currentSortOrder: sortOrder,
    onSort: onSortChange,
  };

  return (
    <ContainerStyled>
      <JourneyHeaderWithAction
        title="Learning items"
        counter={totalCount || 0}
        actionProps={{
          loading: downloadCsvLinkLoading,
          onClick: handleDownloadCSVClick,
          title: 'Download (.csv)',
          variant: 'secondary',
        }}
      />
      <ActionsStyled>
        <ManagementSearchPhrase
          value={searchPhrase}
          onChange={(phrase) => {
            setSearchPhrase(phrase);
          }}
          placeholder="Search learning item"
        />
        <JourneyInsightsTabsLearningItemsTypeFilter
          selectedType={selectedLearningType as string}
          onChange={setSelectedLearningType}
          onTrailingIconClick={() => onClearFilterState('learning type')}
        />
      </ActionsStyled>
      {loading && <JourneyInsightsTabsLearningItemsTableLoadingState />}
      {!loading && lazyLearningItems?.length > 0 && <AutoTable<LearningItemRow, SortBy> {...tableProps} />}
      {!loading && lazyLearningItems.length === 0 && (
        <ManagementEmptyStateAfterProcessed onFiltersClear={() => onClearFilterState('clear all')} />
      )}
    </ContainerStyled>
  );
};
